import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Api from "../util/API";

const normalizeCurrencyCode = (value, fallback = "USD") => {
    if (value && typeof value === "object") {
        return normalizeCurrencyCode(
            value.currency ?? value.code ?? value.value ?? value.defaultCurrencyCode,
            fallback
        );
    }
    const code = String(value || "").trim().toUpperCase();
    return /^[A-Z]{3}$/.test(code) ? code : fallback;
};

// Default to 'BD' if not set
const initialState = {
    products: [],
    status: "idle",
    selectedProduct: {},
    error: null,
    hasMore: true, // 🆕 ডেটা আর আছে কিনা
    currentPage: 1, // 🆕 এখন কোন পেজে আছো
    pagination: {
        page: 1,
        limit: 10,
        total: 0,
        hasMore: true,
    },
};
const BULK_IMPORT_WORKERS = 300;

export const fetchProducts = createAsyncThunk(
    "products/fetchProducts",
    async (args = {}, thunkAPI) => {
        const state = typeof thunkAPI.getState === "function" ? thunkAPI.getState() : null;
        const settings = state?.userSettings || {};

        const country = String(args?.country ?? settings?.shipToCountry ?? "US")
            .trim()
            .toUpperCase();

        const currency = normalizeCurrencyCode(
            args?.currency ?? settings?.currency ?? settings?.selectedCurrency,
            "USD"
        );

        const page = Number.isFinite(Number(args?.page)) ? Number(args.page) : 1;
        const limit = Number.isFinite(Number(args?.limit)) ? Number(args.limit) : 10;
        const append = args?.append !== undefined ? Boolean(args.append) : true;
        const search = String(args?.search ?? '').trim();

        const response = await Api.get(
            `/product/?country=${encodeURIComponent(country)}&currency=${encodeURIComponent(currency)}&page=${page}&limit=${limit}${search ? `&search=${encodeURIComponent(search)}` : ''}`
        );
        return {
            items: response.data?.data || [],
            pagination: response.data?.pagination || { page, limit, total: 0, hasMore: false },
            page,
            append,
        };
    }
);

export const fetchAllProductsAdmin = createAsyncThunk(
    "products/fetchAllProductsAdmin",
    async (args = {}, thunkAPI) => {
        const state = typeof thunkAPI.getState === "function" ? thunkAPI.getState() : null;
        const settings = state?.userSettings || {};

        const priceCountry = String(args?.priceCountry ?? settings?.shipToCountry ?? "US")
            .trim()
            .toUpperCase();

        const currency = normalizeCurrencyCode(
            args?.currency ?? settings?.currency ?? settings?.selectedCurrency,
            "USD"
        );

        const search = String(args?.search ?? '').trim();

        const page = Number.isFinite(Number(args?.page)) ? Number(args.page) : 1;
        const limit = Number.isFinite(Number(args?.limit)) ? Number(args.limit) : 10;

        const response = await Api.get(
            `/product/admin/all?currency=${encodeURIComponent(currency)}&page=${page}&limit=${limit}&priceCountry=${encodeURIComponent(priceCountry)}${search ? `&search=${encodeURIComponent(search)}` : ''}`
        );
        return {
            items: response.data?.data || [],
            pagination: response.data?.pagination || { page, limit, total: 0, hasMore: false },
            page,
            append: false,
        };
    }
);

export const getAllProducts = createAsyncThunk(
    "products/getAllProducts",
    async ({ country, currency = "USD", page = 1, limit = 10 }) => {
        const selectedCurrency = normalizeCurrencyCode(currency, "USD");
        const response = await Api.get(
            `/product/?country=${encodeURIComponent(country)}&currency=${encodeURIComponent(selectedCurrency)}&page=${page}&limit=${limit}`
        );
        return response.data;
    }
);

export const fetchProductById = createAsyncThunk(
    "products/fetchProductById",
    async ({ id, country, currency = "USD" }) => {
        const selectedCurrency = normalizeCurrencyCode(currency, "USD");
        const response = await Api.get(
            "/product/" +
                encodeURIComponent(id) +
                "?country=" +
                encodeURIComponent(country) +
                "&currency=" +
                encodeURIComponent(selectedCurrency)
        );
        return response.data;
    }
);

export const fetchAliProduct = createAsyncThunk(
    "products/fetchAliProduct",
    async (id) => {
        const response = await Api.get(`/product/aliexpress/${id}`);
        return response.data;
    }
);
export const fetchLogistic = createAsyncThunk(
    "products/fetchLogistic",
    async (data) => {
        const response = await Api.post("/logistic", { ...data, currency: "USD" });
        return response.data;
    }
);

export const SaveAliProduct = createAsyncThunk(
    "products/saveAliProduct",
    async (id) => {
        const response = await Api.post(`/product/aliexpress/${id}`);
        return response.data;
    }
);

export const bulkImportAliProducts = createAsyncThunk(
    "products/bulkImportAliProducts",
    async ({ productIds }) => {
        const response = await Api.post("/product/aliexpress/bulk", {
            productIds,
            workers: BULK_IMPORT_WORKERS,
        });
        return response.data;
    }
);

export const startBulkImportAliProductsJob = createAsyncThunk(
    "products/startBulkImportAliProductsJob",
    async ({ productIds, workers = BULK_IMPORT_WORKERS }) => {
        const response = await Api.post("/product/aliexpress/bulk/jobs", {
            productIds,
            workers,
        });
        return response.data;
    }
);

export const getBulkImportAliProductsJob = createAsyncThunk(
    "products/getBulkImportAliProductsJob",
    async ({ jobId, itemLimit = 999 }) => {
        const response = await Api.get(`/product/aliexpress/bulk/jobs/${jobId}?itemLimit=${itemLimit}`);
        return response.data;
    }
);

export const listBulkImportAliProductsJobs = createAsyncThunk(
    "products/listBulkImportAliProductsJobs",
    async ({ status = "active", limit = 10 } = {}) => {
        const response = await Api.get(`/product/aliexpress/bulk/jobs?status=${encodeURIComponent(status)}&limit=${limit}`);
        return response.data;
    }
);

export const cancelBulkImportAliProductsJob = createAsyncThunk(
    "products/cancelBulkImportAliProductsJob",
    async ({ jobId }) => {
        const response = await Api.post(`/product/aliexpress/bulk/jobs/${jobId}/cancel`);
        return response.data;
    }
);

export const updateProduct = createAsyncThunk(
    "products/updateProduct",
    async ({ id, data }) => {
        const response = await Api.put(`/product/${id}`, data);
        return response.data;
    }
);


export const deleteProduct = createAsyncThunk(
    "products/deleteProduct",
    async (productId) => {
        const response = await Api.delete(`/product/${productId}`);
        return response.data;
    }
);

export const bulkDeleteProducts = createAsyncThunk(
    "products/bulkDeleteProducts",
    async (productIds) => {
        const response = await Api.post("/product/bulk-delete", { productIds });
        return response.data;
    }
);

// New async thunks for edit functionality
export const getAllCountriesData = createAsyncThunk(
    "products/getAllCountriesData",
    async (productId) => {
        const response = await Api.get(`/product/edit/${productId}/all-countries`);
        return response.data;
    }
);

export const updateAllCountriesData = createAsyncThunk(
    "products/updateAllCountriesData",
    async ({ productId, ali_data }) => {
        const response = await Api.put(`/product/edit/${productId}/all-countries`, { ali_data });
        return response.data;
    }
);

export const refreshAllCountriesData = createAsyncThunk(
    "products/refreshAllCountriesData",
    async (productId) => {
        const response = await Api.post(`/product/edit/${productId}/refresh`);
        return response.data;
    }
);


const productSlice = createSlice({
    name: "products",
    initialState,
    reducers: {
        clearSelectedProduct: (state) => {
            state.status = "idle";
            state.error = null;
            state.selectedProduct = {};
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProducts.pending, (state) => {
                state.status = "loading";
            })
            .addCase(fetchProducts.fulfilled, (state, action) => {
                const { items, pagination, page, append } = action.payload;

                if (append && page > 1) {
                    state.products = [...state.products, ...items];
                } else {
                    state.products = items;
                }

                state.status = "succeeded";
                state.pagination = pagination || state.pagination;
                state.currentPage = pagination?.page ?? page;
                state.hasMore = pagination?.hasMore ?? items.length > 0;
            })
            .addCase(fetchProducts.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })

            .addCase(fetchAllProductsAdmin.pending, (state) => {
                state.status = "loading";
            })
            .addCase(fetchAllProductsAdmin.fulfilled, (state, action) => {
                const { items, pagination, page } = action.payload;
                state.products = items;
                state.status = "succeeded";
                state.pagination = pagination || state.pagination;
                state.currentPage = pagination?.page ?? page;
                state.hasMore = pagination?.hasMore ?? items.length > 0;
            })
            .addCase(fetchAllProductsAdmin.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })

            .addCase(getAllProducts.pending, (state) => {
                state.status = "loading";
            })
            .addCase(getAllProducts.fulfilled, (state, action) => {
                state.products = action.payload.data || [];
                if (action.payload.pagination) {
                    state.pagination = action.payload.pagination;
                    state.currentPage = action.payload.pagination.page;
                    state.hasMore = action.payload.pagination.hasMore;
                }
                state.status = "succeeded";
            })
            .addCase(getAllProducts.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })

            .addCase(deleteProduct.fulfilled, (state, action) => {
                state.products = state.products.filter(
                    (product) => product.id !== action.payload
                );
            })

            .addCase(fetchProductById.pending, (state) => {
                state.status = "loading";
            })
            .addCase(fetchProductById.fulfilled, (state, action) => {
                state.status = "succeeded";
                console.log("Product fetched by ID:", action.payload);
                state.selectedProduct = action.payload;
            })
            .addCase(fetchProductById.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchAliProduct.pending, (state) => {
                state.status = "loading";
            })
            .addCase(fetchAliProduct.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.error = "";
                state.selectedProduct = action.payload;
            })
            .addCase(fetchAliProduct.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload;
            })
            .addCase(SaveAliProduct.pending, (state) => {
                state.status = "loading";
            })
            .addCase(SaveAliProduct.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.error = "";
                state.selectedProduct = action.payload;
            })
            .addCase(SaveAliProduct.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchLogistic.pending, (state) => {
                state.status = "loading";
            })
            .addCase(fetchLogistic.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.error = "";
                // Assuming the response contains logistic data
                state.selectedProduct.logistic = action.payload;
            })
            .addCase(fetchLogistic.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(updateProduct.pending, (state) => {
                state.status = "loading";
            })
            .addCase(updateProduct.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Update the product in the products array
                const updated = action.payload.data;
                state.products = state.products.map((product) =>
                    product._id === updated._id ? updated : product
                );
                state.selectedProduct = updated;
            })
            .addCase(updateProduct.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(getAllCountriesData.pending, (state) => {
                state.status = "loading";
            })
            .addCase(getAllCountriesData.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.error = "";
                state.selectedProduct = action.payload.data;
            })
            .addCase(getAllCountriesData.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(updateAllCountriesData.pending, (state) => {
                state.status = "loading";
            })
            .addCase(updateAllCountriesData.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.error = "";
                // Keep any existing ali_data in state.selectedProduct.
                // Backend update response doesn't always include ali_data.
                state.selectedProduct = {
                    ...(state.selectedProduct || {}),
                    ...(action.payload?.data || {})
                };
            })
            .addCase(updateAllCountriesData.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(refreshAllCountriesData.pending, (state) => {
                state.status = "loading";
            })
            .addCase(refreshAllCountriesData.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.error = "";
                state.selectedProduct = action.payload.data;
            })
            .addCase(refreshAllCountriesData.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            });
    },
});

export const selectProducts = (state) => state.products.products;
export const selectProductById = (state, productId) =>
    state.products.products.find((product) => product.id === productId);

// Selector to get two random products from the products list
export const selectTwoRandomProducts = (state) => {
    const products = state.products.products;
    if (products.length < 2) {
        return products; // Return all products if less than 2
    }

    // Create a copy of the products array to avoid mutating the original
    const shuffled = [...products].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 2);
};

// Selector to get three random products for slides (different from side banners)
export const selectThreeRandomProductsForSlides = (state) => {
    const products = state.products.products;
    if (products.length < 3) {
        return products; // Return all products if less than 3
    }

    // Create a copy of the products array to avoid mutating the original
    const shuffled = [...products].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3);
};

export const { clearSelectedProduct } = productSlice.actions;
export default productSlice.reducer;
